import React, { useEffect, useState, useRef } from 'react';
import './donation-details.scss';
import _ from 'lodash';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import DonorManagerAPI from '../../../services/donorAPI';
import { DEFAULT_ACTIVE_TAB_INDEX, DONATION_TYPES, RECEIPT_TYPES, TAB_HEADER_LIST, URL_PATHNAME } from '../../../utils/Utils';
import LoadingComponent from '../../../common/Loading';
import moment from 'moment';
import DonationInfo from './components/donation-info/donation-info';
import CertificateDonation from './components/certificate-donation/certificate-donation';
import ReceiptDonation from './components/receipt-donation/receipt-donation';
import PaymentMethod from './components/payment-method/payment-method';
import { formatStringToNumber } from '../../../utils/functionCommon';
import { useTranslation, withTranslation } from 'react-i18next';

const DonationDetailsPage = (props: any) => {
  let { token, id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [donation, setDonation] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [stripeSubscriptionUrl, setStripeSubscriptionUrl] = useState('');
  const toast = useRef(null);
  const [hiddenReceiptInfo, setHiddenReceiptInfo] = useState(false);
  const [donationReceipts, setDonationReceipts] = useState<any>();
  const [donationCertificate, setDonationCertificate] = useState<any>(null);
  const { t, i18n } = useTranslation();
  type Language = 'en' | 'de';
  const currentLanguage = i18n.language as Language;

  const [caclValues, setCaclValues] = useState({
    amount: 0,
    area: 0,
    co2: 0,
  });
  const getInitialTabIndex = () => {
    const tabParam = searchParams.get('tab');
    const isValidTabParam = TAB_HEADER_LIST.some((item) => item.code === tabParam);
    return isValidTabParam ? Number(tabParam) : DEFAULT_ACTIVE_TAB_INDEX;
  };
  const [activeIndex, setActiveIndex] = useState(getInitialTabIndex());

  const isSubscriptionDonation = (donation: any) => {
    return +donation?.rhythm > 0;
  };

  const isFinishedSubsDonation = (donation: any) => {
    return !!donation?.finished_date;
  };

  const tabHeaders = [
    {
      label: TAB_HEADER_LIST[0]?.name[currentLanguage] || TAB_HEADER_LIST[0]?.name.de,
      index: 0,
    },
    {
      label: TAB_HEADER_LIST[1]?.name[currentLanguage] || TAB_HEADER_LIST[1]?.name.de,
      index: 1,
    },
    {
      label: TAB_HEADER_LIST[2]?.name[currentLanguage] || TAB_HEADER_LIST[2]?.name.de,
      index: 2,
      hidden: hiddenReceiptInfo,
    },
    {
      label: TAB_HEADER_LIST[3]?.name[currentLanguage] || TAB_HEADER_LIST[3]?.name.de,
      index: 3,
      hidden: !isSubscriptionDonation(donation) || isFinishedSubsDonation(donation),
    },
  ];

  const setValueCacl = (donation: any) => {
    const sumAmount =
      donation.rhythm > 0 ? (_.sumBy(donation.sub_donations || [], 'donation_money') || 0) + (donation.donation_money || 0) : donation.donation_money;

    const sumArea = donation.rhythm > 0 ? (_.sumBy(donation.sub_donations || [], 'area') || 0) + (donation.area || 0) : donation.area;

    const sumCo2 =
      donation.rhythm > 0 ? (_.sumBy(donation.sub_donations || [], 'amount_kg_co2') || 0) + (donation.amount_kg_co2 || 0) : donation.amount_kg_co2;

    setCaclValues({
      amount: sumAmount,
      area: sumArea,
      co2: sumCo2,
    });
  };

  const getDonationDetails = async (id: string) => {
    try {
      setIsLoading(true);
      const donation: any = await getDonationAsync(id);
      donation.receipt_name = getReceiptName(donation);

      const subDonation =
        donation && donation.sub_donations
          ? donation.sub_donations.map((itemDonation: any) => {
              return {
                ...itemDonation,
                donation_money: formatStringToNumber(itemDonation.donation_money),
                area: formatStringToNumber(itemDonation.area),
                amount_kg_co2: formatStringToNumber(itemDonation.amount_kg_co2),
              };
            })
          : [];

      const formatDonation = {
        ...donation,
        donation_money: formatStringToNumber(donation.donation_money),
        area: formatStringToNumber(donation.area),
        amount_kg_co2: formatStringToNumber(donation.amount_kg_co2),
        sub_donations: subDonation,
      };

      setValueCacl(formatDonation);
      setDonation(formatDonation);
      if (formatDonation.rhythm === 0) {
        setDonationReceipts([
          {
            payment_date: formatDonation.payment_date,
            total_amount: formatDonation.donation_money,
            year: new Date(formatDonation.payment_date).getFullYear(),
            isParent: true,
            currency_unit: formatDonation.currency_unit,
            match_funding_value: formatDonation.match_funding_value,
          },
        ]);
      } else {
        groupDonationByYear(formatDonation.sub_donations, formatDonation);
      }

      if (formatDonation.rhythm === 0) {
        setDonationCertificate([
          {
            payment_date: formatDonation.payment_date,
            total_amount: formatDonation.donation_money,
            year: new Date(formatDonation.payment_date).getFullYear(),
            isParent: true,
            currency_unit: formatDonation.currency_unit,
            area: formatDonation.area,
          },
        ]);
      } else {
        groupDonationCertificateByYear(formatDonation.sub_donations, formatDonation);
      }

      checkReceiptInfo(formatDonation);
      if (isSubscriptionDonation(formatDonation)) {
        let url = await getStripeSubscriptionUrl(formatDonation.payment_currency_code);
        setStripeSubscriptionUrl(url);
      }
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR: ', e);
    }
  };

  const getStripeSubscriptionUrl = async (currency_code: string) => {
    try {
      const { data: linkResponse } = await DonorManagerAPI.getStripeLink(currency_code, token || '');
      if (linkResponse === 'Session is expired!') {
        navigate(URL_PATHNAME.DONOR_PORTAL);
      }

      return linkResponse?.portal_url;
    } catch (error) {
      console.log('getDonorDonationAsync: ' + error);
    }

    return null;
  };

  const checkReceiptInfo = (donation: any) => {
    const hiddenReceiptInfo = donation.donation_type === DONATION_TYPES.CODE && donation.code_batch?.receipt_type !== RECEIPT_TYPES.REDEEMER;
    setHiddenReceiptInfo(hiddenReceiptInfo);
  };

  const groupDonationByYear = (donations: any, donation: any) => {
    let donationReceiptsByYears: any = [];
    const donationReceiptByYears = {
      payment_date: donation.payment_date,
      total_amount: donation.donation_money,
      year: new Date(donation.payment_date).getFullYear(),
      isParent: true,
      currency_unit: donation.currency_unit,
      coupon_value: donation?.coupon_value,
      match_funding_value: donation?.match_funding_value,
    };

    donationReceiptsByYears.push(donationReceiptByYears);
    const group: any = groupedByYear(donations);
    const donationByYear = createDonationByYears(group, donationReceiptByYears?.currency_unit);

    donationReceiptsByYears = donationReceiptsByYears.concat(donationByYear);
    setDonationReceipts(donationReceiptsByYears);
  };

  const groupDonationCertificateByYear = (donations: any, donation: any) => {
    let donationCertificatesByYears: any = [];
    const donationReceiptByYears = {
      payment_date: donation.payment_date,
      total_amount: donation.donation_money,
      year: new Date(donation.payment_date).getFullYear(),
      isParent: true,
      currency_unit: donation.currency_unit,
      area: donation.area,
    };
    donationCertificatesByYears.push(donationReceiptByYears);
    const group: any = groupedByYear(donations);

    const donationByYear = createDonationByYears(group, donationReceiptByYears?.currency_unit);
    donationCertificatesByYears = donationCertificatesByYears.concat(donationByYear);

    setDonationCertificate(donationCertificatesByYears);
  };

  const groupedByYear = (donations: any) => {
    const today = moment();
    const january31 = moment().month(0).date(31);
    const currentYear = today.year();
    const groupedByYear: any = Object.values(donations).reduce((result: any, item: any) => {
      const year = new Date(item.payment_date).getFullYear();
      if (!isNaN(year)) {
        if (!result[year]) {
          result[year] = [];
        }
        if (year < currentYear) {
          if (!(year === currentYear - 1 && today.isBefore(january31))) {
            result[year].push(item);
          }
        }
      }
      return result;
    }, {});

    return groupedByYear;
  };

  const createDonationByYears = (group: any, currencyUnit: any) => {
    let donationByYears = [];
    for (const key in group) {
      if (group.hasOwnProperty(key)) {
        const value = group[key];
        if (value && value.length > 0) {
          const filterValue = value.filter((item: any) => item.status === 'completed');
          const receiptByYear = {
            payment_date: `${parseInt(key) + 1}-01-31T00:00:00.000Z`,
            total_amount: _.sumBy(filterValue, (d: any) => +d.donation_money) || 0,
            year: key,
            currency_unit: currencyUnit,
            area: _.sumBy(filterValue, (d: any) => +d.area) || 0,
            match_funding_value: 0,
          };
          donationByYears.push(receiptByYear);
        }
      }
    }

    return donationByYears;
  };

  const getDonationAsync = async (donation_id: string) => {
    try {
      const { data: donorDetailData } = await DonorManagerAPI.getDonationDetail(donation_id, token || '');

      if (donorDetailData === 'Session is expired!') {
        navigate(URL_PATHNAME.DONOR_PORTAL);
      }

      return donorDetailData.donation;
    } catch (error) {
      console.log('getDonationAsync: ' + error);
    }

    return {};
  };

  const getReceiptName = (donation: any) => {
    if (donation) {
      return donation?.stripe_donor_name ? donation.stripe_donor_name : donation?.payment_first_name + ' ' + donation?.payment_last_name || '-';
    }

    return '-';
  };

  const formatDate = (date_time: any) => {
    return moment(date_time).format('DD.MM.YYYY');
  };

  useEffect(() => {
    getDonationDetails(id || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Toast ref={toast} />
      <div className={`donation-details-page`}>
        {!isLoading && !!donation ? (
          <div className="donation-details-container">
            <div className={`donation-details-body donor-tab-header bg-gray ${activeIndex ? 'bg-linear' : 'bg-white'}`}>
              <div className="aw_container donation-details-tabs-container">
                {tabHeaders.map((item: any, index: number) => {
                  return (
                    !item.hidden && (
                      <div
                        key={index}
                        className={`tab-details ${index === activeIndex ? 'tabs-details-active' : ''}`}
                        onClick={() => setActiveIndex(index)}
                      >
                        {item.label}
                      </div>
                    )
                  );
                })}
              </div>
            </div>
            {activeIndex === 0 ? (
              <DonationInfo
                donation={donation}
                isSubscriptionDonation={isSubscriptionDonation}
                formatDate={formatDate}
                isFinishedSubsDonation={isFinishedSubsDonation}
                caclValues={caclValues}
                token={token}
              />
            ) : (
              <></>
            )}
            {activeIndex === 1 ? (
              <CertificateDonation
                formatDate={formatDate}
                donationCertificate={donationCertificate}
                isLoading={isLoading}
                donation={donation}
                token={token}
                setDonation={setDonation}
                id={id}
                toast={toast}
                isSubscriptionDonation={isSubscriptionDonation}
                getDonationDetails={getDonationDetails}
              />
            ) : (
              <></>
            )}
            {activeIndex === 2 && !hiddenReceiptInfo ? (
              <ReceiptDonation
                donation={donation}
                formatDate={formatDate}
                token={token}
                id={id}
                toast={toast}
                getDonationDetails={getDonationDetails}
                donationReceipts={donationReceipts}
                isLoading={isLoading}
                isSubscriptionDonation={isSubscriptionDonation}
              />
            ) : (
              <></>
            )}
            {activeIndex === 3 ? (
              <PaymentMethod
                isSubscriptionDonation={isSubscriptionDonation}
                donation={donation}
                stripeSubscriptionUrl={stripeSubscriptionUrl}
                isFinishedSubsDonation={isFinishedSubsDonation}
              />
            ) : (
              <></>
            )}
            <div className="donation-details-footer"></div>
          </div>
        ) : (
          <LoadingComponent />
        )}
      </div>
    </>
  );
};

export default withTranslation('language')(DonationDetailsPage);
